import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { AuthProvider, AuthInit } from './auth/Auth.tsx';
import { ProfileImageProvider } from './helpers/hooks/profilePhoto.tsx';
import { LogoProvider } from './helpers/hooks/LogoContext.tsx';
import { DomainStatusProvider } from './helpers/hooks/DomainStatus.tsx';
import { ThemeProvider } from './helpers/hooks/ThemeContext.tsx';
import { API_URL, APP_ENV, DESCOPE_PROJECT_ID } from '../src/auth/_requests';
import * as Sentry from '@sentry/react';
import { scrubSentryEvent } from './helpers/utils/safeSentryError.ts';
import { Toaster } from 'react-hot-toast';
import { MFAProvider } from './helpers/hooks/mfaListContext.tsx';
import {
  loadClarity,
  loadGoogleAnalytics,
  loadMetaPixel,
  loadMixpanel,
  loadPostHog,
} from './helpers/utils/utils.ts';
import { AuthProvider as DescopeAuthProvider } from '@descope/react-sdk';

import { FONT_BODY } from './constants/fontConstants';
import { NotificationListenerProvider } from './helpers/hooks/NotificationContext.tsx';
import { PropertyProvider } from './helpers/hooks/creditPropertySelected.tsx';
import { UserProfileProvider } from './helpers/hooks/userProfile.jsx';

if (APP_ENV === 'production' || APP_ENV === 'staging') {
  loadGoogleAnalytics();
  loadClarity();
  loadPostHog();
  loadMixpanel();
  loadMetaPixel();
}
if (APP_ENV === 'production') {
  Sentry.init({
    dsn: 'https://7851c2ff2e77e41e4db333a2a094a823@o4506470979993600.ingest.us.sentry.io/4508845976584192',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [new RegExp('^' + API_URL + '/')],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Scrub only genuinely sensitive fields ourselves (exact-key match) so debugging
    // context like `error`, `errorDescription`, `errCode`, `cardType` reaches Sentry.
    sendDefaultPii: false,
    normalizeDepth: 8,
    beforeSend: scrubSentryEvent,
  });
}
ReactDOM.createRoot(document.getElementById('root')!).render(
  <DescopeAuthProvider projectId={DESCOPE_PROJECT_ID}>
    <AuthProvider>
      <NotificationListenerProvider>
        <AuthInit>
          <ProfileImageProvider>
            <ThemeProvider>
              <DomainStatusProvider>
                <LogoProvider>
                  <MFAProvider>
                    <PropertyProvider>
                      <UserProfileProvider>
                        <Toaster
                          position='top-center'
                          reverseOrder={false}
                          toastOptions={{
                            style: {
                              fontFamily: FONT_BODY,
                            },
                          }}
                        />
                        <BrowserRouter>
                          <App />
                        </BrowserRouter>
                      </UserProfileProvider>
                    </PropertyProvider>
                  </MFAProvider>
                </LogoProvider>
              </DomainStatusProvider>
            </ThemeProvider>
          </ProfileImageProvider>
        </AuthInit>
      </NotificationListenerProvider>
    </AuthProvider>
  </DescopeAuthProvider>
);
